import { html, css, LitElement } from 'lit-element';
import { ref, getDownloadURL } from 'firebase/storage';
import { collection, getDocs, query, where } from 'firebase/firestore';

import '@material/mwc-button';

import { db, storage } from './intrivity_app.js';
import { hasRole } from './utility.js';
import { a11yStyles } from './a11y.js';
import { t, Localized, formatDate, formatNumber } from './i18n.js';
import { TRAINING_CATEGORIES } from './admin_training_docs.js';

// Training Documents -- what trainers and program managers see: the documents an
// administrator has RELEASED, and nothing else. Managing them is the admin
// panel (admin_training_docs.js).
//
// The query filters on status == 'released' because firestore.rules only lets
// these roles read released rows; a query that could return a draft is refused
// outright. The status filter is therefore a requirement, not just a convenience,
// and the list is sorted here so no composite index is needed.
export class IntrivityTrainingDocs extends Localized(LitElement) {

    static get styles() {
        return [a11yStyles, css`
            :host {
                display: block;
                font-family: 'Muli', sans-serif;
                color: #222;
                max-width: 900px;
                margin: 0 auto;
                padding: 30px 20px 60px;
            }
            h1 { font-size: 1.8em; margin: 0 0 6px; }
            .lede { color: #5c5c5c; max-width: 62ch; margin: 0 0 24px; line-height: 1.5; }
            .error { color: #a3341f; }
            .table-scroll { overflow-x: auto; }
            table { border-collapse: collapse; width: 100%; min-width: 560px; font-size: 0.95em; }
            caption { text-align: left; padding: 6px 0; color: #5c5c5c; }
            th, td { border-bottom: 1px solid #e4e4e4; padding: 10px; text-align: left; vertical-align: top; }
            thead th { border-bottom: 1px solid #bbb; font-size: 0.82em; text-transform: uppercase; letter-spacing: 0.04em; }
            .meta { color: #5c5c5c; font-size: 0.9em; font-weight: normal; }
            td.actions { white-space: nowrap; }
        `];
    }

    static get properties() {
        return {
            user_profile: { type: Object },
            _docs: { type: Array },
            _loading: { type: Boolean },
            _error: { type: String },
        };
    }

    constructor() {
        super();
        this._docs = [];
        this._loading = true;
        this._error = '';
    }

    connectedCallback() {
        super.connectedCallback();
        this._load();
    }

    _canView() {
        return hasRole('trainer', this.user_profile) || hasRole('program_manager', this.user_profile);
    }

    async _load() {
        this._loading = true;
        this._error = '';
        try {
            const snap = await getDocs(query(collection(db, 'training_documents'), where('status', '==', 'released')));
            this._docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
                .sort((x, y) => String(y.releasedAt || '').localeCompare(String(x.releasedAt || '')));
        } catch (e) {
            this._error = t('training.loadError', { message: e.message || e });
        } finally {
            this._loading = false;
        }
    }

    async _open(entry) {
        this._error = '';
        if (!entry.storagePath) {
            this._error = t('compliance.noFile', { title: entry.title });
            return;
        }
        try {
            const url = await getDownloadURL(ref(storage, entry.storagePath));
            window.open(url, '_blank', 'noopener');
        } catch (e) {
            this._error = t('compliance.openFailed', { message: e.message || e });
        }
    }

    _categoryLabel(id) {
        const found = TRAINING_CATEGORIES.find((c) => c.id === id);
        return found ? found.label : id;
    }

    _formatDate(iso) {
        if (!iso) return '';
        const d = new Date(iso);
        return isNaN(d) ? '' : formatDate(d, { dateStyle: 'short' });
    }

    _formatSize(bytes) {
        if (!bytes && bytes !== 0) return '';
        if (bytes < 1024) return formatNumber(bytes, { style: 'unit', unit: 'byte', unitDisplay: 'narrow' });
        if (bytes < 1024 * 1024) return formatNumber(Math.round(bytes / 1024), { style: 'unit', unit: 'kilobyte', unitDisplay: 'short' });
        return formatNumber(bytes / (1024 * 1024), { style: 'unit', unit: 'megabyte', unitDisplay: 'short', maximumFractionDigits: 1 });
    }

    _renderTable() {
        if (this._loading) return html`<p role="status">${t('training.loading')}</p>`;
        if (!this._docs.length) return html`<p>${t('training.viewer.none')}</p>`;
        return html`
            <div class="table-scroll">
                <table>
                    <caption>${t('training.viewer.caption', { count: this._docs.length })}</caption>
                    <thead>
                        <tr>
                            <th scope="col">${t('compliance.colDocument')}</th>
                            <th scope="col">${t('compliance.colCategory')}</th>
                            <th scope="col">${t('training.colReleased')}</th>
                            <th scope="col">${t('compliance.colSize')}</th>
                            <th scope="col"><span class="sr-only">${t('compliance.colActions')}</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        ${this._docs.map((entry) => html`
                            <tr>
                                <th scope="row">
                                    ${entry.title}
                                    ${entry.notes ? html`<div class="meta">${entry.notes}</div>` : html``}
                                    <div class="meta">${entry.filename}</div>
                                </th>
                                <td>${this._categoryLabel(entry.category)}</td>
                                <td class="meta">${this._formatDate(entry.releasedAt)}</td>
                                <td class="meta">${this._formatSize(entry.size)}</td>
                                <td class="actions">
                                    <mwc-button dense outlined
                                        label=${t('compliance.open')}
                                        @click=${() => this._open(entry)}></mwc-button>
                                </td>
                            </tr>
                        `)}
                    </tbody>
                </table>
            </div>
        `;
    }

    render() {
        if (!this._canView()) return html``;
        return html`
            <h1>${t('training.viewer.title')}</h1>
            <p class="lede">${t('training.viewer.intro')}</p>
            <div class="${this._error ? 'error' : ''}" role="status" aria-live="polite">${this._error}</div>
            ${this._renderTable()}
        `;
    }
}

if (!customElements.get('intrivity-training-docs')) {
    customElements.define('intrivity-training-docs', IntrivityTrainingDocs);
}
